import React, {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useFocusTrap } from "../hooks/useFocusTrap";

type PopoverMenuProps = {
  children: React.ReactNode;
  /**
   * Menu body. Pass a render function to receive a `close()` callback — use it
   * from items that open an overlay (modal/drawer), which otherwise leave the
   * popover open since the click stays inside the panel.
   */
  content: React.ReactNode | ((close: () => void) => React.ReactNode);
  className?: string;
  contentClassName?: string;
  place?: "top" | "right" | "bottom" | "left";
  /**
   * Horizontal alignment for the `top`/`bottom` placements (ignored by
   * `left`/`right`, which are already positioned on the horizontal axis).
   *
   * - `"end"` (default) — panel's right edge meets the trigger's right edge, so
   *   it opens leftward. Kept as the default because every existing consumer
   *   was built against it.
   * - `"start"` — panel's left edge meets the trigger's left edge, so it opens
   *   rightward. This is what the DS PopoverMenu does for `place="bottom"`
   *   ("Below the trigger, left-aligned with its left edge"), and what the
   *   Sub IB report's row kebabs are designed to do.
   */
  align?: "start" | "end";
  /**
   * When true, PopoverMenu will not wrap children in a button.
   * Instead, it clones the child element and attaches click handlers directly.
   * Use this when children is already an interactive element (button, anchor, etc.).
   */
  asChild?: boolean;
  /**
   * - `"click"` (default) — the trigger toggles the panel on click only.
   * - `"hover"` — pointing at the trigger opens the panel and pointing away
   *   from both trigger and panel closes it. Click still toggles, so keyboard
   *   users (Enter/Space) and touch devices keep a way in.
   */
  openOn?: "click" | "hover";
  /**
   * Makes the panel modal while open, for panels that would otherwise trail
   * their trigger across sticky content such as the app header: page scroll is
   * locked, focus moves into the panel and stays there, and returns to the
   * trigger on close. Leave it off for ordinary menus and selects.
   */
  lockPageScroll?: boolean;
};

const GAP = 4;

// Trigger and panel are separated by GAP, so the pointer is over neither while
// travelling between them. Closing on a delay keeps the panel reachable.
const HOVER_CLOSE_DELAY_MS = 150;

function computePosition(
  triggerRect: DOMRect,
  popoverRect: DOMRect,
  place: NonNullable<PopoverMenuProps["place"]>,
  align: NonNullable<PopoverMenuProps["align"]> = "end",
) {
  let top = 0;
  let left = 0;

  const horizontal =
    align === "start"
      ? triggerRect.left
      : triggerRect.right - popoverRect.width;

  switch (place) {
    case "bottom":
      top = triggerRect.bottom + GAP;
      left = horizontal;
      break;
    case "top":
      top = triggerRect.top - popoverRect.height - GAP;
      left = horizontal;
      break;
    case "left":
      top = triggerRect.top;
      left = triggerRect.left - popoverRect.width - GAP;
      break;
    case "right":
      top = triggerRect.top;
      left = triggerRect.right + GAP;
      break;
  }

  // Clamp so the popover stays within the viewport
  top = Math.max(
    GAP,
    Math.min(top, window.innerHeight - popoverRect.height - GAP),
  );
  left = Math.max(
    GAP,
    Math.min(left, window.innerWidth - popoverRect.width - GAP),
  );

  return { top, left };
}

export const PopoverMenu: React.FC<PopoverMenuProps> = ({
  children,
  content,
  className,
  contentClassName,
  place = "bottom",
  align = "end",
  asChild = false,
  openOn = "click",
  lockPageScroll = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();

    setPosition(computePosition(triggerRect, popoverRect, place, align));
    setIsPositioned(true);
  }, [place, align]);

  const close = () => {
    setIsOpen(false);
    setIsPositioned(false);
  };

  // Recalculate position before paint when popover opens.
  // Using useEffect is safe here because the popover starts with opacity-0
  // until isPositioned becomes true, avoiding any visible flicker.
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  // Reposition on scroll / resize so it stays anchored
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  useBodyScrollLock(isOpen && lockPageScroll);
  useFocusTrap(popoverRef, isOpen && isPositioned && lockPageScroll);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }

      setIsOpen(false);
      setIsPositioned(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setIsPositioned(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  const hoverHandlers =
    openOn === "hover"
      ? {
          onMouseEnter: () => {
            cancelScheduledClose();
            setIsOpen(true);
          },
          onMouseLeave: () => {
            cancelScheduledClose();
            closeTimer.current = setTimeout(close, HOVER_CLOSE_DELAY_MS);
          },
        }
      : {};

  const handleClick = () => (isOpen ? close() : setIsOpen(true));

  const renderTrigger = () => {
    if (asChild && isValidElement(children)) {
      const childElement = children as React.ReactElement<
        Record<string, unknown>
      >;
      // For <button> children without an explicit type, default to "button"
      // to prevent unintended form submissions (buttons default to type="submit")
      const typeProps =
        childElement.type === "button" && childElement.props.type == null
          ? { type: "button" as const }
          : {};

      // Clone child and attach click handler and aria attributes
      return cloneElement(childElement, {
        ...typeProps,
        onClick: (e: React.MouseEvent) => {
          handleClick();
          // Call original onClick if present
          const originalOnClick = childElement.props.onClick;
          if (typeof originalOnClick === "function") {
            originalOnClick(e);
          }
        },
        "aria-haspopup": "menu",
        "aria-expanded": isOpen,
      });
    }

    return (
      <button
        type="button"
        onClick={handleClick}
        className="cursor-pointer"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {children}
      </button>
    );
  };

  return (
    <div
      ref={triggerRef}
      className={`relative inline-block ${className ?? ""}`}
      {...hoverHandlers}
    >
      {renderTrigger()}

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            data-testid="popover_panel"
            {...hoverHandlers}
            style={{
              top: position.top,
              left: position.left,
            }}
            className={`fixed z-50 rounded-lg bg-surface-primary shadow-lg shadow-stroke-primary transition-opacity duration-75 ${isPositioned ? "opacity-100" : "opacity-0"} ${contentClassName ?? ""}`}
          >
            {typeof content === "function" ? content(close) : content}
          </div>,
          document.body,
        )}
    </div>
  );
};
